import React, { useState, useEffect, useCallback } from 'react';
import { Loader, AlertTriangle, RefreshCw, ChevronLeft, ChevronRight } from 'lucide-react';
import { neonQuery } from '../services/api';

// VIP SIGN-UPS - who actually joined after one of our offers, and on what term.
//
// Greg, 2026-10-01: "I want to see the customer, the date they signed up, their expiration date and
// their renewal date ... what I'm trying to do is verify that they're signing up the way that we
// think they're signing up. Some of these are 4-1 renewals, some have been 10-1 renewals, today
// they'll start with 11-1 renewals for the different campaigns."
//
// So the top strip counts sign-ups by renewal date, and a row is flagged when its term is not the
// one its campaign should produce, or it is set not to renew. The rule lives in signupsBaseSql
// (lib/vip-term-watch.js, which also emails the off-term ones): a win-back runs to the next 31 March, a Delivered/Placed offer to
// the last day of the sign-up month a year on.

const PAGE = 30;
const CAMPAIGN = { winback: 'VIP Win-back', delivered: 'Delivered by day', placed: 'Placed by day' };

const TH = { padding: '8px 10px', textAlign: 'left', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.4, color: 'var(--gray-500)', borderBottom: '1px solid var(--gray-200)', whiteSpace: 'nowrap' };
const TD = { padding: '8px 10px', borderBottom: '1px solid var(--gray-100)', fontSize: 13, verticalAlign: 'top' };
const BTN = (disabled) => ({
  display: 'inline-flex', alignItems: 'center', gap: 4, padding: '6px 12px', borderRadius: 6, fontSize: 12,
  fontWeight: 600, background: '#fff', border: '1px solid var(--gray-300)', color: disabled ? 'var(--gray-400)' : 'var(--gray-700)',
  cursor: disabled ? 'default' : 'pointer', fontFamily: 'inherit', opacity: disabled ? 0.6 : 1,
});

// Greg's rule for anything a person reads: Aug 21, 2025, not 2025-08-21.
const longDate = (iso) => {
  const m = String(iso || '').match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!m) return '';
  return ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'][Number(m[2]) - 1] + ' ' + Number(m[3]) + ', ' + m[1];
};

const mdy = (iso) => {
  if (!iso) return '';
  const [y, m, d] = String(iso).slice(0, 10).split('-');
  return Number(m) + '/' + Number(d) + '/' + y.slice(2);
};

// A button that reads as the text it replaced: the count on a box, the name in a row.
const LINK = { background: 'none', border: 'none', padding: 0, margin: 0, fontFamily: 'inherit', cursor: 'pointer', textAlign: 'left', textDecoration: 'underline' };

export default function VipSignupsPanel({ navigateTo }) {
  const [page, setPage] = useState(0);
  // Rachael, 2026-10-09: "there is a box that says RENEWS 4/1/27 1 to check but it's not
  // clickable, I have to search for it." Clicking the count narrows the list to the rows that
  // box is counting; `only` is that box's renewal date.
  const [only, setOnly] = useState(null);
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async (p, renews) => {
    setLoading(true); setErr(null);
    try {
      const q = { limit: PAGE, offset: p * PAGE };
      if (renews) { q.renews = renews; q.flagged = true; }
      setData(await neonQuery('signups_recent', q));
    }
    catch (e) { setErr(e.message); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(page, only); }, [load, page, only]);
  // When the box shows "1 to check" and that one record loads, go straight there.
  useEffect(() => {
    if (only && navigateTo && data && data.total === 1 && data.rows.length === 1) {
      openCustomer(data.rows[0]);
    }
  }, [only, data, navigateTo]); // eslint-disable-line react-hooks/exhaustive-deps
  const showOnly = (renews) => { setPage(0); setOnly(renews); };
  // "Nothing to do here." Rachael, 2026-10-09: a win-back member set not to renew who does not
  // want to be, "can we get a way to clear him? We can add in notes & explain why." It asks why,
  // and cancelling clears nothing. The note stays on the row for the next person.
  const clearFlag = async (r, name) => {
    const note = window.prompt(
      `Clear the flag on ${name}?\n\nUse this when you have looked into it and there is nothing to do. `
      + 'Your note stays on the row with your name, so the next person knows why.\n\nWhat did you find?', '');
    if (note === null || !note.trim()) return;
    try {
      await neonQuery('signup_check_set', { customerId: r.customer_id, expires: r.expires, autoRenew: r.auto_renew, note: note.trim() });
      load(page, only);
    } catch (e) { window.alert('Could not clear that one: ' + e.message); }
  };
  const unclearFlag = async (r) => {
    try { await neonQuery('signup_check_clear', { customerId: r.customer_id }); load(page, only); }
    catch (e) { window.alert('Could not put the flag back: ' + e.message); }
  };
  const openCustomer = (r) => navigateTo && navigateTo('customer-search', null, { email: r.email, autoSelect: true });

  const total = data ? data.total : 0;
  const pages = Math.max(1, Math.ceil(total / PAGE));
  const rows = data ? data.rows : [];

  // Renewal date -> how many, and how many of those look wrong.
  const byRenewal = {};
  (data ? data.groups : []).forEach((g) => {
    const k = g.renews;
    if (!byRenewal[k]) byRenewal[k] = { renews: k, n: 0, bad: 0, modes: [] };
    byRenewal[k].n += g.n;
    byRenewal[k].bad += g.to_check != null ? g.to_check : g.off_term + g.not_renewing;
    byRenewal[k].modes.push(`${g.n} ${CAMPAIGN[g.mode] || g.mode || 'other'}`);
  });
  const strip = Object.values(byRenewal).sort((a, b) => a.renews.localeCompare(b.renews));

  return (
    <div>
      {strip.length > 0 && (
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 14 }}>
          {strip.map((g) => (
            <div key={g.renews} style={{ background: only === g.renews ? '#fff5f5' : '#fff', border: '1px solid ' + (only === g.renews ? '#b42318' : 'var(--gray-200)'), borderRadius: 10, padding: '10px 14px', minWidth: 170 }}>
              <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.4, color: 'var(--gray-500)' }}>Renews {mdy(g.renews)}</div>
              <div style={{ fontSize: 22, fontWeight: 800, color: 'var(--navy)' }}>{g.n}</div>
              <div style={{ fontSize: 11.5, color: 'var(--gray-500)' }}>{g.modes.join(' · ')}</div>
              {g.bad > 0 && (
                <button type="button" title="Show just these" onClick={() => showOnly(only === g.renews ? null : g.renews)}
                  style={{ ...LINK, display: 'block', fontSize: 11.5, fontWeight: 700, color: '#b42318', marginTop: 2 }}>
                  <AlertTriangle size={11} /> {g.bad} to check
                </button>
              )}
            </div>
          ))}
        </div>
      )}

      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10, fontSize: 13, color: 'var(--gray-600)' }}>
        {only ? (
          <>
            <span><b style={{ color: '#b42318' }}>{total}</b> to check, renewing {mdy(only)}</span>
            <button style={BTN(false)} onClick={() => showOnly(null)}>Show all sign-ups</button>
          </>
        ) : (
          <span><b style={{ color: 'var(--navy)' }}>{total}</b> sign-ups after an offer in the last {data ? data.days : 120} days, newest first</span>
        )}
        <div style={{ flex: 1 }} />
        <button style={BTN(false)} onClick={() => load(page)}><RefreshCw size={12} /> Refresh</button>
      </div>

      {err && <div style={{ color: '#b42318', fontSize: 13, marginBottom: 10 }}>{err}</div>}
      {loading && !data ? (
        <div style={{ padding: 30, textAlign: 'center', color: 'var(--gray-500)' }}><Loader size={16} className="spin" /> Loading sign-ups...</div>
      ) : !rows.length ? (
        <div style={{ padding: 30, textAlign: 'center', color: 'var(--gray-500)' }}>{only ? 'Nothing left to check for this renewal date.' : 'No sign-ups in this window.'}</div>
      ) : (
        <div style={{ overflowX: 'auto', background: '#fff', border: '1px solid var(--gray-200)', borderRadius: 10, opacity: loading ? 0.6 : 1 }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                <th style={TH}>Customer</th><th style={TH}>Signed up</th><th style={TH}>Expires</th>
                <th style={TH}>Renews</th><th style={TH}>Campaign</th><th style={TH}>Rep</th><th style={TH}>Check</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => {
                const offTerm = r.expires !== r.expected_expires;
                const flags = [];
                if (offTerm) flags.push(`Expected to expire ${mdy(r.expected_expires)} for ${CAMPAIGN[r.mode] || 'this campaign'}`);
                if (!r.auto_renew) flags.push('Set NOT to renew');
                if (!r.active) flags.push('No longer active');
                const name = [r.firstname, r.lastname].filter(Boolean).join(' ').trim() || '(no name)';
                const price = Number(r.offer_price);
                const cleared = !!r.checked_by && flags.length > 0;
                const red = flags.length > 0 && !cleared;
                return (
                  <tr key={r.customer_id} style={{ background: red ? '#fff5f5' : undefined }}>
                    <td style={TD}>
                      {navigateTo
                        ? <button type="button" title="Open this customer" onClick={() => openCustomer(r)} style={{ ...LINK, fontSize: 13, fontWeight: 700, color: 'var(--navy)' }}>{name}</button>
                        : <div style={{ fontWeight: 700, color: 'var(--navy)' }}>{name}</div>}
                      <div style={{ fontSize: 11.5, color: 'var(--gray-500)' }}>{r.email}</div>
                    </td>
                    <td style={{ ...TD, whiteSpace: 'nowrap' }}>
                      {mdy(r.signed_up)}
                      {r.first_joined && r.first_joined !== r.signed_up && (
                        <div style={{ fontSize: 11, color: 'var(--gray-400)' }}>first joined {mdy(r.first_joined)}</div>
                      )}
                    </td>
                    <td style={{ ...TD, whiteSpace: 'nowrap', color: offTerm ? '#b42318' : undefined, fontWeight: offTerm ? 700 : 400 }}>{mdy(r.expires)}</td>
                    <td style={{ ...TD, whiteSpace: 'nowrap', fontWeight: 700, color: r.auto_renew ? 'var(--navy)' : '#b42318' }}>
                      {r.auto_renew ? mdy(r.renews) : 'will not renew'}
                    </td>
                    <td style={TD}>
                      {CAMPAIGN[r.mode] || r.mode || '—'}
                      <div style={{ fontSize: 11, color: 'var(--gray-400)' }}>
                        offered {mdy(r.offered_on)}{Number.isFinite(price) ? (price > 0 ? ' at $' + price.toFixed(2) : ' free') : ''}
                      </div>
                    </td>
                    <td style={TD}>{String(r.rep_name || '').split(' ')[0]}</td>
                    <td style={{ ...TD, fontSize: 12, color: red ? '#b42318' : cleared ? 'var(--gray-600)' : '#0b8043', fontWeight: 600, maxWidth: 340 }}>
                      {flags.length ? flags.map((f) => <div key={f}><AlertTriangle size={11} /> {f}</div>) : 'as expected'}
                      {red && (
                        <button type="button" onClick={() => clearFlag(r, name)} style={{ ...LINK, fontSize: 11.5, fontWeight: 400, color: 'var(--gray-500)', marginTop: 3 }}>Nothing to do, clear it</button>
                      )}
                      {cleared && (
                        <div style={{ marginTop: 4, fontWeight: 400, color: 'var(--gray-700)' }}>
                          <b style={{ color: '#0b8043' }}>Cleared by {String(r.checked_by).split(' ')[0]}, {longDate(r.checked_on)}.</b> {r.checked_note}
                          <div><button type="button" onClick={() => unclearFlag(r)} style={{ ...LINK, fontSize: 11, color: 'var(--gray-500)' }}>Put the flag back</button></div>
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {total > PAGE && (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 12, marginTop: 14, fontSize: 13, color: 'var(--gray-600)' }}>
          <button style={BTN(page === 0)} disabled={page === 0} onClick={() => setPage((p) => Math.max(0, p - 1))}><ChevronLeft size={13} /> Newer</button>
          <span>Page <b>{page + 1}</b> of {pages} &middot; {page * PAGE + 1}&ndash;{Math.min(total, (page + 1) * PAGE)} of {total}</span>
          <button style={BTN(page >= pages - 1)} disabled={page >= pages - 1} onClick={() => setPage((p) => Math.min(pages - 1, p + 1))}>Older <ChevronRight size={13} /></button>
        </div>
      )}
    </div>
  );
}
